import {
  CheckCircle2,
  ChevronRight,
  FileVideo,
  Gauge,
  Image as ImageIcon,
  Link,
  LoaderCircle,
  Play,
  UploadCloud,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from 'react';

import { ErrorCode } from '../../common/enum/error-code.enum';
import { useApp } from '../components/app-context';
import { Button } from '../components/button';
import { Dialog } from '../components/dialog';
import { Divider } from '../components/divider';
import { EpisodeList, hasEpisodeAired } from '../components/episode-list';
import { Input } from '../components/input';
import { MediaSourceDialog } from '../components/media-source-dialog';
import { ProgressBar } from '../components/progress-bar';
import { VideoPlayer } from '../components/video-player';
import { t } from '../i18n';
import { useStore } from '../hooks/use-store';
import { $accessToken, $autoMarkWatched, $autoPlayNextEpisode, $currentUser } from '../store/app.store';
import type {
  AnimeSubject,
  Episode,
  EpisodeCollectionType,
  EpisodePage,
  SubjectDetail,
  UserEpisodeCollectionPage,
} from '../types/bgm';
import type {
  CreateDownloadResult,
  DownloadFileInfo,
  DownloadInfo,
  MediaResourcePlayResult,
  PlaybackMediaInfo,
  PlaybackSnapshot,
  PlaybackSubtitleUpdate,
  WatchLaterPage,
} from '../types/page';
import { safeUrl } from '../utils/url';

const MEDIA_EXTENSIONS = new Set(['mkv', 'mp4', 'm4v', 'mov', 'webm', 'avi', 'ts', 'mpeg', 'mpg']);
const FILES_TIMEOUT_MS = 120_000;
const WATCH_PROGRESS_SAVE_INTERVAL_MS = 10_000;
type PreparationStage = 'idle' | 'resolving' | 'metadata' | 'selecting' | 'media';

const delay = (milliseconds: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, milliseconds));

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** exponent).toFixed(exponent > 1 ? 1 : 0)} ${units[exponent]}`;
};

const isMediaFile = (file: DownloadFileInfo): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return Boolean(extension && MEDIA_EXTENSIONS.has(extension));
};

const matchesEpisodeNumber = (file: DownloadFileInfo, episodeNumber: number): boolean => {
  if (!Number.isFinite(episodeNumber) || episodeNumber <= 0) {
    return false;
  }
  const escapedEpisode = String(episodeNumber).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const patterns = [
    new RegExp(`(?:^|[^a-z0-9])(?:ep(?:isode)?[ ._-]*)0*${escapedEpisode}(?:v\\d+)?(?:[^0-9]|$)`, 'i'),
    new RegExp(`\\[0*${escapedEpisode}(?:v\\d+)?\\]`, 'i'),
    new RegExp(`(?:^|[^0-9])0*${escapedEpisode}(?:v\\d+)?(?:[^0-9]|$)`, 'i'),
  ];
  return patterns.some((pattern) => pattern.test(file.name));
};

interface PlaybackSubjectData {
  episodeStatuses: Record<number, EpisodeCollectionType>;
  subject: SubjectDetail;
  episodes: EpisodePage;
}

interface PlaybackWatchLaterItem {
  episode: Episode;
  subject: AnimeSubject;
}

export const PlaybackPage = () => {
  const { api, bgm, bgmAuth, navigate, showToast } = useApp();
  const autoMarkWatched = useStore($autoMarkWatched);
  const autoPlayNextEpisode = useStore($autoPlayNextEpisode);
  const currentUser = useStore($currentUser);
  const deepLinkStarted = useRef<string | undefined>(undefined);
  const markedEpisodeKeys = useRef(new Set<string>());
  const advancedEpisodeKeys = useRef(new Set<string>());
  const progressSave = useRef<{ key: string; progress: number; savedAt: number } | undefined>(undefined);
  const deepLinkSearch = location.search;
  const deepLinkParameters = new URLSearchParams(deepLinkSearch);
  const sourceSubjectId = Number(deepLinkParameters.get('subject'));
  const watchLaterMode = deepLinkParameters.get('watchLater') === '1';
  const [url, setUrl] = useState('');
  const [torrentFile, setTorrentFile] = useState<File>();
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [download, setDownload] = useState<DownloadInfo>();
  const [files, setFiles] = useState<DownloadFileInfo[]>([]);
  const [selectedFileIndex, setSelectedFileIndex] = useState<number>();
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [media, setMedia] = useState<PlaybackMediaInfo>();
  const [snapshot, setSnapshot] = useState<PlaybackSnapshot>();
  const [preparationStage, setPreparationStage] = useState<PreparationStage>('idle');
  const [playbackSubject, setPlaybackSubject] = useState<PlaybackSubjectData>();
  const [playbackSubjectLoading, setPlaybackSubjectLoading] = useState(false);
  const [mediaItem, setMediaItem] = useState<PlaybackWatchLaterItem>();
  const [activeEpisodeId, setActiveEpisodeId] = useState<number>();
  const [initialPlaybackTime, setInitialPlaybackTime] = useState(0);
  const [watchLaterEpisodeIds, setWatchLaterEpisodeIds] = useState<Set<number>>(() => new Set());
  const [watchLaterUpdatingEpisodeIds, setWatchLaterUpdatingEpisodeIds] = useState<Set<number>>(() => new Set());
  const [watchLaterQueue, setWatchLaterQueue] = useState<PlaybackWatchLaterItem[]>([]);
  const [watchLaterQueueLoading, setWatchLaterQueueLoading] = useState(false);

  const saveWatchProgress = useCallback(
    (progress: number, duration: number, force: boolean): void => {
      if (
        !download ||
        !media ||
        !activeEpisodeId ||
        !Number.isInteger(sourceSubjectId) ||
        sourceSubjectId <= 0 ||
        progress < 1
      ) {
        return;
      }
      const now = Date.now();
      const key = `${sourceSubjectId}:${activeEpisodeId}:${download.uuid}:${media.fileIndex}`;
      const previous = progressSave.current?.key === key ? progressSave.current : undefined;
      if (
        previous &&
        ((!force && now - previous.savedAt < WATCH_PROGRESS_SAVE_INTERVAL_MS) ||
          (force && Math.abs(previous.progress - progress) < 0.5))
      ) {
        return;
      }
      progressSave.current = { key, progress, savedAt: now };
      void api<{ updated: true }>('/api/watch-progress', {
        method: 'PUT',
        body: JSON.stringify({
          bangumiSubjectId: sourceSubjectId,
          bangumiEpisodeId: activeEpisodeId,
          downloadUuid: download.uuid,
          fileIndex: media.fileIndex,
          progress: Math.min(progress, duration),
          duration,
        }),
      }).catch(() => undefined);
    },
    [activeEpisodeId, api, download, media, sourceSubjectId],
  );

  const autoMarkActiveEpisode = useCallback(async (): Promise<void> => {
    if (!autoMarkWatched || !Number.isInteger(sourceSubjectId) || sourceSubjectId <= 0 || !activeEpisodeId) {
      return;
    }
    const episodeKey = `${sourceSubjectId}:${activeEpisodeId}`;
    if (markedEpisodeKeys.current.has(episodeKey)) {
      return;
    }
    markedEpisodeKeys.current.add(episodeKey);
    if (!currentUser?.bgmToken) {
      showToast(t('autoMarkRequiresBangumiToken'), 'error');
      return;
    }
    try {
      await bgmAuth(`/v0/users/-/collections/${sourceSubjectId}/episodes`, {
        method: 'PATCH',
        body: JSON.stringify({ episode_id: [activeEpisodeId], type: 2 }),
      });
      showToast(t('episodeAutoMarked'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    }
  }, [activeEpisodeId, autoMarkWatched, bgmAuth, currentUser?.bgmToken, showToast, sourceSubjectId]);

  const mediaFiles = useMemo(
    () => files.filter(isMediaFile).sort((left, right) => right.length - left.length),
    [files],
  );

  useEffect(() => {
    if (!Number.isInteger(sourceSubjectId) || sourceSubjectId <= 0) {
      setPlaybackSubject(undefined);
      setPlaybackSubjectLoading(false);
      setMediaItem(undefined);
      setActiveEpisodeId(undefined);
      setWatchLaterEpisodeIds(new Set());
      setWatchLaterUpdatingEpisodeIds(new Set());
      return;
    }
    let active = true;
    setPlaybackSubject(undefined);
    setPlaybackSubjectLoading(true);
    setWatchLaterEpisodeIds(new Set());
    setWatchLaterUpdatingEpisodeIds(new Set());
    const episodeCollections = currentUser?.bgmToken
      ? bgmAuth<UserEpisodeCollectionPage>(
          `/v0/users/-/collections/${sourceSubjectId}/episodes?limit=1000&episode_type=0`,
        ).catch(() => undefined)
      : Promise.resolve(undefined);
    const watchLater = api<WatchLaterPage>(`/api/watch-later?subjectId=${sourceSubjectId}&limit=1000`).catch(
      (): WatchLaterPage => ({ data: [], total: 0 }),
    );
    void Promise.all([
      bgm<SubjectDetail>(`/v0/subjects/${sourceSubjectId}`),
      bgm<EpisodePage>(`/v0/episodes?subject_id=${sourceSubjectId}&type=0&limit=200`),
      episodeCollections,
      watchLater,
    ])
      .then(([subject, episodes, collections, watchLaterPage]) => {
        if (active) {
          setPlaybackSubject({
            subject,
            episodes,
            episodeStatuses: Object.fromEntries(
              (collections?.data ?? []).map((collection) => [collection.episode.id, collection.type]),
            ),
          });
          setWatchLaterEpisodeIds(new Set(watchLaterPage.data.map((item) => item.bangumiEpisodeId)));
        }
      })
      .catch((reason: unknown) => {
        if (active) {
          showToast(reason instanceof Error ? reason.message : String(reason), 'error');
        }
      })
      .finally(() => {
        if (active) {
          setPlaybackSubjectLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api, bgm, bgmAuth, currentUser?.bgmToken, showToast, sourceSubjectId]);

  useEffect(() => {
    if (!watchLaterMode) {
      setWatchLaterQueue([]);
      setWatchLaterQueueLoading(false);
      return;
    }
    let active = true;
    setWatchLaterQueueLoading(true);
    void api<WatchLaterPage>('/api/watch-later?limit=1000')
      .then(async (page) => {
        const subjects = new Map<number, Promise<AnimeSubject>>();
        const subjectFor = (subjectId: number): Promise<AnimeSubject> => {
          const existing = subjects.get(subjectId);
          if (existing) {
            return existing;
          }
          const request = bgm<AnimeSubject>(`/v0/subjects/${subjectId}`);
          subjects.set(subjectId, request);
          return request;
        };
        const hydrated = await Promise.allSettled(
          page.data.map(async (record): Promise<PlaybackWatchLaterItem> => ({
            subject: await subjectFor(record.bangumiSubjectId),
            episode: await bgm<Episode>(`/v0/episodes/${record.bangumiEpisodeId}`),
          })),
        );
        if (active) {
          setWatchLaterQueue(hydrated.flatMap((item) => (item.status === 'fulfilled' ? [item.value] : [])));
        }
      })
      .catch((reason: unknown) => {
        if (active) {
          setWatchLaterQueue([]);
          showToast(reason instanceof Error ? reason.message : String(reason), 'error');
        }
      })
      .finally(() => {
        if (active) {
          setWatchLaterQueueLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api, bgm, showToast, watchLaterMode]);

  useEffect(() => {
    if (!download || download.status !== 'downloading') {
      return;
    }
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(
      `${protocol}//${location.host}/api/playback/${download.uuid}?token=${encodeURIComponent($accessToken.get())}`,
    );
    socket.onmessage = (event) => {
      const next = JSON.parse(String(event.data)) as PlaybackSnapshot;
      setSnapshot(next);
      setDownload((current) =>
        current
          ? {
              ...current,
              status: next.status,
              downloadedBytes: next.downloaded,
              totalBytes: next.total,
              error: next.error ?? null,
              errorCode: next.errorCode ?? null,
            }
          : current,
      );
    };
    return () => socket.close();
  }, [download?.status, download?.uuid]);

  useEffect(() => {
    if (!media || media.subtitlesComplete) {
      return;
    }
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(
      `${protocol}//${location.host}/api/playback/${media.uuid}/files/${media.fileIndex}/subtitles/events?token=${encodeURIComponent($accessToken.get())}`,
    );
    socket.onmessage = (event) => {
      const update = JSON.parse(String(event.data)) as PlaybackSubtitleUpdate;
      if (update.type !== 'subtitleManifest') {
        return;
      }
      setMedia((current) =>
        current && current.uuid === update.uuid && current.fileIndex === update.fileIndex
          ? {
              ...current,
              subtitles: update.manifest.subtitles,
              subtitlesComplete: update.manifest.complete,
            }
          : current,
      );
    };
    return () => socket.close();
  }, [media?.fileIndex, media?.subtitlesComplete, media?.uuid]);

  const waitForFiles = async (uuid: string): Promise<DownloadFileInfo[]> => {
    const deadline = Date.now() + FILES_TIMEOUT_MS;
    while (Date.now() < deadline) {
      const nextFiles = await api<DownloadFileInfo[]>(`/api/downloads/${uuid}/files`);
      if (nextFiles.length > 0) {
        return nextFiles;
      }
      const current = await api<DownloadInfo>(`/api/downloads/${uuid}`);
      if (current.status === 'failed') {
        throw new Error(
          current.errorCode === ErrorCode.DOWNLOAD_METADATA_TIMEOUT
            ? t('torrentMetadataTimeout')
            : current.error || t('downloadFailed'),
        );
      }
      await delay(500);
    }
    throw new Error(t('torrentMetadataTimeout'));
  };

  const resolveSource = async (body: BodyInit): Promise<void> => {
    setSubmitting(true);
    setPreparationStage('resolving');
    setMedia(undefined);
    setDownload(undefined);
    setSnapshot(undefined);
    try {
      const result = await api<CreateDownloadResult>('/api/downloads', { method: 'POST', body });
      setDownload(result.download);
      setPreparationStage('metadata');
      const nextFiles = await waitForFiles(result.download.uuid);
      const playableFiles = nextFiles.filter(isMediaFile);
      if (playableFiles.length === 0) {
        throw new Error(t('noMediaFiles'));
      }
      setFiles(nextFiles);
      const onlyMediaFile = playableFiles[0];
      if (playableFiles.length === 1 && onlyMediaFile) {
        await startPlayback(result.download, onlyMediaFile.fileIndex, false);
      } else {
        setSelectedFileIndex(undefined);
        setPreparationStage('selecting');
        setFileDialogOpen(true);
      }
    } catch (reason) {
      setPreparationStage('idle');
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const submitUrl = (event: FormEvent): void => {
    event.preventDefault();
    if (!url.trim()) {
      return;
    }
    void resolveSource(JSON.stringify({ url: url.trim() }));
  };

  const uploadTorrent = (file: File | undefined): void => {
    if (!file || submitting) {
      return;
    }
    if (!file.name.toLowerCase().endsWith('.torrent')) {
      showToast(t('torrentFileRequired'), 'error');
      return;
    }
    setTorrentFile(file);
    const formData = new FormData();
    formData.set('torrent', file);
    void resolveSource(formData);
  };

  const chooseDroppedFile = (event: DragEvent<HTMLLabelElement>): void => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    uploadTorrent(file);
  };

  const startPlayback = async (
    targetDownload: DownloadInfo | undefined = download,
    fileIndex: number | undefined = selectedFileIndex,
    restoreFileDialogOnError = true,
  ): Promise<void> => {
    if (!targetDownload || fileIndex === undefined) {
      return;
    }
    setFileDialogOpen(false);
    setSubmitting(true);
    setPreparationStage('media');
    try {
      setMedia(
        await api<PlaybackMediaInfo>(`/api/playback/${targetDownload.uuid}/files/${fileIndex}`, { method: 'POST' }),
      );
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
      if (restoreFileDialogOnError) {
        setPreparationStage('selecting');
        setFileDialogOpen(true);
      } else {
        setPreparationStage('idle');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const prepareDownload = async (
    targetDownload: DownloadInfo,
    episodeNumber: number,
    preferredFileIndex?: number,
  ): Promise<void> => {
    setSubmitting(true);
    setPreparationStage('metadata');
    setFileDialogOpen(false);
    setSelectedFileIndex(undefined);
    setMedia(undefined);
    setSnapshot(undefined);
    setDownload(targetDownload);
    try {
      const nextFiles = await waitForFiles(targetDownload.uuid);
      const playableFiles = nextFiles.filter(isMediaFile);
      if (playableFiles.length === 0) {
        throw new Error(t('noMediaFiles'));
      }
      setFiles(nextFiles);
      const episodeMatches = playableFiles.filter((file) => matchesEpisodeNumber(file, episodeNumber));
      const selectedFile =
        playableFiles.find((file) => file.fileIndex === preferredFileIndex) ??
        (episodeMatches.length === 1 ? episodeMatches[0] : playableFiles.length === 1 ? playableFiles[0] : undefined);
      if (selectedFile) {
        await startPlayback(targetDownload, selectedFile.fileIndex, false);
      } else {
        setSelectedFileIndex(undefined);
        setPreparationStage('selecting');
        setFileDialogOpen(true);
      }
    } catch (reason) {
      setPreparationStage('idle');
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (deepLinkStarted.current === deepLinkSearch) {
      return;
    }
    deepLinkStarted.current = deepLinkSearch;
    const search = new URLSearchParams(deepLinkSearch);
    const uuid = search.get('download');
    if (!uuid) {
      return;
    }
    const episodeNumber = Number(search.get('episode'));
    const episodeId = Number(search.get('episodeId'));
    const fileIndex = Number(search.get('fileIndex'));
    const position = Number(search.get('position'));
    setActiveEpisodeId(Number.isInteger(episodeId) && episodeId > 0 ? episodeId : undefined);
    setInitialPlaybackTime(Number.isFinite(position) && position > 0 ? position : 0);
    void (async () => {
      setSubmitting(true);
      setPreparationStage('metadata');
      try {
        const targetDownload = await api<DownloadInfo>(`/api/downloads/${encodeURIComponent(uuid)}`);
        await prepareDownload(
          targetDownload,
          episodeNumber,
          Number.isInteger(fileIndex) && fileIndex >= 0 ? fileIndex : undefined,
        );
      } catch (reason) {
        setPreparationStage('idle');
        setSubmitting(false);
        showToast(reason instanceof Error ? reason.message : String(reason), 'error');
      }
    })();
  }, [api, deepLinkSearch, showToast]);

  const playEpisodeResource = async (
    result: MediaResourcePlayResult,
    episode: Episode,
    subjectId: number,
  ): Promise<void> => {
    setMediaItem(undefined);
    setActiveEpisodeId(episode.id);
    setInitialPlaybackTime(0);
    const search = new URLSearchParams({
      download: result.download.uuid,
      episode: String(episode.ep ?? episode.sort),
      episodeId: String(episode.id),
      subject: String(subjectId),
    });
    if (watchLaterMode) {
      search.set('watchLater', '1');
    }
    const nextSearch = `?${search.toString()}`;
    deepLinkStarted.current = nextSearch;
    navigate(`/playback${nextSearch}`, true);
    await prepareDownload(result.download, episode.ep ?? episode.sort);
  };

  const toggleWatchLater = async (episode: Episode): Promise<void> => {
    if (!playbackSubject || watchLaterUpdatingEpisodeIds.has(episode.id)) {
      return;
    }
    const saved = watchLaterEpisodeIds.has(episode.id);
    setWatchLaterEpisodeIds((current) => {
      const next = new Set(current);
      if (saved) {
        next.delete(episode.id);
      } else {
        next.add(episode.id);
      }
      return next;
    });
    setWatchLaterUpdatingEpisodeIds((current) => new Set(current).add(episode.id));
    try {
      if (saved) {
        await api(`/api/watch-later/${episode.id}`, { method: 'DELETE' });
      } else {
        await api('/api/watch-later', {
          method: 'PUT',
          body: JSON.stringify({
            bangumiSubjectId: playbackSubject.subject.id,
            bangumiEpisodeId: episode.id,
          }),
        });
      }
    } catch (reason) {
      setWatchLaterEpisodeIds((current) => {
        const next = new Set(current);
        if (saved) {
          next.add(episode.id);
        } else {
          next.delete(episode.id);
        }
        return next;
      });
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setWatchLaterUpdatingEpisodeIds((current) => {
        const next = new Set(current);
        next.delete(episode.id);
        return next;
      });
    }
  };

  const playNextEpisode = (): void => {
    if (watchLaterMode) {
      if (!activeEpisodeId) {
        return;
      }
      const episodeKey = `watch-later:${activeEpisodeId}`;
      if (advancedEpisodeKeys.current.has(episodeKey)) {
        return;
      }
      const currentIndex = watchLaterQueue.findIndex((item) => item.episode.id === activeEpisodeId);
      const nextItem = currentIndex >= 0 ? watchLaterQueue[currentIndex + 1] : undefined;
      if (nextItem) {
        advancedEpisodeKeys.current.add(episodeKey);
        setMediaItem(nextItem);
      }
      return;
    }
    if (!autoPlayNextEpisode || !playbackSubject || !activeEpisodeId) {
      return;
    }
    const episodeKey = `${playbackSubject.subject.id}:${activeEpisodeId}`;
    if (advancedEpisodeKeys.current.has(episodeKey)) {
      return;
    }
    const episodes = [...playbackSubject.episodes.data].sort((left, right) => left.sort - right.sort);
    const currentIndex = episodes.findIndex((episode) => episode.id === activeEpisodeId);
    const nextEpisode = currentIndex >= 0 ? episodes[currentIndex + 1] : undefined;
    if (!nextEpisode || !hasEpisodeAired(nextEpisode.airdate)) {
      return;
    }
    advancedEpisodeKeys.current.add(episodeKey);
    setActiveEpisodeId(nextEpisode.id);
    setInitialPlaybackTime(0);
    setMediaItem({ subject: playbackSubject.subject, episode: nextEpisode });
  };

  const videoLink = media ? new URL(media.streamUrl, location.origin).href : '';
  const videoDownloadLink = media
    ? new URL(`/api/downloads/${media.uuid}/files/${media.fileIndex}/download`, location.origin).href
    : '';

  const copyVideoLink = async (): Promise<void> => {
    if (!videoLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(videoLink);
      showToast(t('videoLinkCopied'), 'success');
    } catch {
      showToast(t('copyFailed'), 'error');
    }
  };

  const downloadVideoFile = (): void => {
    if (!videoDownloadLink) {
      return;
    }
    const link = document.createElement('a');
    link.href = videoDownloadLink;
    link.hidden = true;
    document.body.append(link);
    link.click();
    link.remove();
  };

  const downloadM3uPlaylist = (): void => {
    if (!media || !videoLink) {
      return;
    }
    const title = media.fileName.replaceAll('\n', ' ').replaceAll('\r', ' ');
    const playlist = new Blob([`#EXTM3U\n#EXTINF:-1,${title}\n${videoLink}\n`], {
      type: 'audio/x-mpegurl;charset=utf-8',
    });
    const playlistUrl = URL.createObjectURL(playlist);
    const link = document.createElement('a');
    const baseName = media.fileName.replace(/\.[^.]+$/, '').replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_') || 'BGMer';
    link.href = playlistUrl;
    link.download = `${baseName}.m3u`;
    link.hidden = true;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(playlistUrl));
  };

  const fileSelectionDialog = (
    <Dialog
      open={fileDialogOpen}
      title={t('selectMediaFile')}
      description={
        <div className="mt-3 grid max-h-90 gap-2 overflow-y-auto">
          {mediaFiles.map((file) => (
            <label
              className={`grid cursor-pointer grid-cols-[16px_20px_minmax(0,1fr)] items-center gap-2.5 rounded-[10px] border border-(--line) bg-(--surface-2) p-3 ${
                selectedFileIndex === file.fileIndex
                  ? 'border-(--input-accent) bg-[color-mix(in_srgb,var(--input-accent)_8%,var(--surface-2))]'
                  : ''
              }`.trim()}
              key={file.fileIndex}
            >
              <input
                className="size-4 accent-(--input-accent)"
                type="radio"
                name="media-file"
                checked={selectedFileIndex === file.fileIndex}
                onChange={() => setSelectedFileIndex(file.fileIndex)}
              />
              <FileVideo className="size-5 text-(--muted)" />
              <span className="m-0 min-w-0">
                <strong className="block break-all text-[11px] leading-5">{file.name}</strong>
                <small className="mt-1 block text-[9px] text-(--muted)">{formatBytes(file.length)}</small>
              </span>
            </label>
          ))}
        </div>
      }
      confirmText={t('startPlayback')}
      confirmDisabled={selectedFileIndex === undefined}
      loading={submitting}
      onCancel={() => {
        setFileDialogOpen(false);
        setPreparationStage('idle');
      }}
      onConfirm={() => void startPlayback()}
    />
  );

  if (media || preparationStage !== 'idle') {
    const progress = snapshot?.progress ?? (download ? download.downloadedBytes / Math.max(download.totalBytes, 1) : 0);
    const downloadCompleted = snapshot?.status === 'completed' || download?.status === 'completed';
    const preparationMessage =
      preparationStage === 'resolving'
        ? t('parsingTorrent')
        : preparationStage === 'metadata'
          ? t('readingTorrentFiles')
          : preparationStage === 'selecting'
            ? t('selectMediaFile')
            : t('preparingPlayback');
    return (
      <section className="w-full">
        <header className="mb-5 flex items-end justify-between gap-4 max-[700px]:flex-col max-[700px]:items-stretch">
          <div className="min-w-0 flex-1">
            <p className="eyebrow">PLAYER</p>
            <h1 className="m-0 line-clamp-2 w-full max-w-200 text-2xl max-[700px]:max-w-full max-[700px]:text-lg">
              {media?.fileName ?? download?.name ?? t('playbackTitle')}
            </h1>
          </div>
        </header>
        <div className="video-shell">
          {media ? (
            <VideoPlayer
              key={media.streamUrl}
              src={media.streamUrl}
              initialTime={initialPlaybackTime}
              subtitles={media.subtitles}
              subtitlesComplete={media.subtitlesComplete}
              onProgress={saveWatchProgress}
              onEnded={() => void playNextEpisode()}
              onCompleted={
                autoMarkWatched && sourceSubjectId > 0 && activeEpisodeId
                  ? () => void autoMarkActiveEpisode()
                  : undefined
              }
              onCopyVideoLink={() => void copyVideoLink()}
              onDownloadM3uPlaylist={downloadM3uPlaylist}
              onDownloadVideoFile={downloadVideoFile}
              onError={() => showToast(t('nativePlaybackUnsupported'), 'error')}
            />
          ) : (
            <div className="playback-status">
              <LoaderCircle className="spin" />
              <strong>{preparationMessage}</strong>
            </div>
          )}
        </div>
        {download ? (
          <div className="playback-stats mt-4 grid grid-cols-[180px_130px_minmax(0,1fr)] gap-3 max-[700px]:grid-cols-2">
            <div>
              <Gauge />
              <span>{t('downloadSpeed')}</span>
              <strong>{formatBytes(snapshot?.downloadSpeed ?? 0)}/s</strong>
            </div>
            <div>
              <Users />
              <span>{t('peers')}</span>
              <strong>{snapshot?.peers ?? 0}</strong>
            </div>
            <div className="playback-progress-card">
              <span>{t('downloadProgress')}</span>
              {downloadCompleted ? (
                <span className="playback-complete-chip">
                  <CheckCircle2 />
                  {t('downloadCompleted')}
                </span>
              ) : (
                <strong>{Math.round(progress * 100)}%</strong>
              )}
              <ProgressBar className="playback-progress-bar col-span-2 mt-2" size="medium" value={progress} max={1} />
            </div>
          </div>
        ) : null}
        {watchLaterMode ? (
          <section className="mt-6 min-w-0">
            <h2 className="mb-3 flex items-center gap-2 text-sm">
              <span>{t('watchLater')}</span>
              {!watchLaterQueueLoading ? (
                <small className="text-[10px] font-normal text-(--muted)">{watchLaterQueue.length}</small>
              ) : null}
            </h2>
            {watchLaterQueueLoading ? (
              <div className="grid min-h-20 place-items-center text-(--muted)">
                <LoaderCircle className="size-5 spin" />
              </div>
            ) : watchLaterQueue.length ? (
              <div className="flex max-w-full gap-2.5 overflow-x-auto pb-2">
                {watchLaterQueue.map((item) => {
                  const image = safeUrl(
                    item.subject.images?.large ?? item.subject.images?.common ?? item.subject.images?.medium,
                  );
                  const active = item.episode.id === activeEpisodeId;
                  return (
                    <Button
                      variant="plain"
                      className={`flex w-56 shrink-0 cursor-pointer items-center gap-2.5 rounded-xl border p-2 text-left transition-colors ${
                        active
                          ? 'border-(--input-accent) bg-[color-mix(in_srgb,var(--input-accent)_9%,var(--surface))]'
                          : 'border-(--line) bg-(--surface) hover:border-[color-mix(in_srgb,var(--input-accent)_38%,var(--line))]'
                      }`.trim()}
                      aria-current={active ? 'true' : undefined}
                      key={item.episode.id}
                      onClick={() => setMediaItem(item)}
                    >
                      <span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-(--surface-2) text-(--muted)">
                        {image ? (
                          <img
                            className="size-full object-cover"
                            src={image}
                            alt=""
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <ImageIcon className="size-4" />
                        )}
                      </span>
                      <span className="grid min-w-0 flex-1 gap-1">
                        <strong className="truncate text-xs">{item.subject.name_cn || item.subject.name}</strong>
                        <small className="truncate text-[10px] text-(--muted)">
                          EP.{item.episode.sort}
                          {item.episode.name_cn || item.episode.name
                            ? ` · ${item.episode.name_cn || item.episode.name}`
                            : ''}
                        </small>
                      </span>
                    </Button>
                  );
                })}
              </div>
            ) : (
              <div className="grid min-h-20 place-items-center text-xs text-(--muted)">{t('noWatchLater')}</div>
            )}
          </section>
        ) : null}
        {playbackSubjectLoading ? (
          <div className="playback-subject-loading">
            <LoaderCircle className="spin" />
          </div>
        ) : null}
        {playbackSubject ? (
          <section className="playback-subject-section">
            <Button
              variant="plain"
              className="playback-subject-overview"
              onClick={() => navigate(`/subjects/${playbackSubject.subject.id}`)}
            >
              <span className="playback-subject-cover">
                {safeUrl(playbackSubject.subject.images?.large ?? playbackSubject.subject.images?.common) ? (
                  <img
                    src={safeUrl(playbackSubject.subject.images?.large ?? playbackSubject.subject.images?.common)}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <ImageIcon />
                )}
              </span>
              <span className="playback-subject-copy m-0 grid min-w-0 gap-1">
                <small className="eyebrow">ANIME</small>
                <strong>{playbackSubject.subject.name_cn || playbackSubject.subject.name}</strong>
                {playbackSubject.subject.name_cn ? <span>{playbackSubject.subject.name}</span> : null}
                <span className="playback-subject-summary">{playbackSubject.subject.summary}</span>
              </span>
              <ChevronRight />
            </Button>
            <section className="detail-section">
              <h2>
                {t('episodes')}
                <small>{playbackSubject.episodes.total}</small>
              </h2>
              <EpisodeList
                activeEpisodeId={activeEpisodeId}
                episodes={playbackSubject.episodes.data}
                episodeStatuses={playbackSubject.episodeStatuses}
                watchLaterEpisodeIds={watchLaterEpisodeIds}
                watchLaterUpdatingEpisodeIds={watchLaterUpdatingEpisodeIds}
                onToggleWatchLater={(episode) => void toggleWatchLater(episode)}
                onPlay={(episode) => setMediaItem({ subject: playbackSubject.subject, episode })}
              />
            </section>
          </section>
        ) : null}
        {mediaItem ? (
          <MediaSourceDialog
            subject={mediaItem.subject}
            episode={mediaItem.episode}
            onCancel={() => setMediaItem(undefined)}
            onPlay={(result, episode) => playEpisodeResource(result, episode, mediaItem.subject.id)}
          />
        ) : null}
        {fileSelectionDialog}
      </section>
    );
  }

  return (
    <section className="w-full">
      <div className="hero-copy compact-hero">
        <p className="eyebrow">PLAYER</p>
        <h1>{t('playbackTitle')}</h1>
      </div>
      <section className="grid w-full min-w-0 content-start gap-5 rounded-[14px] border border-(--line) bg-(--surface) p-6 max-[700px]:p-4">
        <form className="grid min-w-0 gap-4" onSubmit={submitUrl}>
          <div className="flex items-start gap-3">
            <Link className="mt-0.5 size-6 shrink-0 text-(--input-accent)" />
            <div>
              <h2 className="m-0 text-lg">{t('torrentUrl')}</h2>
              <p className="mt-1 mb-0 text-[11px] leading-5 text-(--muted)">{t('torrentUrlDescription')}</p>
            </div>
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 max-[700px]:grid-cols-1">
            <Input
              clearable
              wrapperClassName="min-w-0"
              icon={<Link />}
              value={url}
              placeholder={t('torrentUrlPlaceholder')}
              clearLabel={t('clearInput')}
              onClear={() => setUrl('')}
              onChange={(event) => setUrl(event.target.value)}
            />
            <Button className="min-h-11.75 max-[700px]:w-full" type="submit" disabled={!url.trim() || submitting}>
              {submitting ? <LoaderCircle className="size-4! spin" /> : <Play className="size-4!" />}
              {t('parseAndPlay')}
            </Button>
          </div>
        </form>
        <Divider />
        <section className="grid min-w-0 gap-4">
          <div className="flex items-start gap-3">
            <UploadCloud className="mt-0.5 size-6 shrink-0 text-(--input-accent)" />
            <div>
              <h2 className="m-0 text-lg">{t('torrentUpload')}</h2>
              <p className="mt-1 mb-0 text-[11px] leading-5 text-(--muted)">{t('torrentUploadDescription')}</p>
            </div>
          </div>
          <label
            className={`mt-2 grid min-h-50 cursor-pointer place-items-center gap-2 rounded-xl border border-dashed border-(--default-button-border) bg-(--surface-2) p-5 text-center transition-[border-color,background] duration-180 ${
              dragging ? 'border-(--input-accent) bg-[color-mix(in_srgb,var(--input-accent)_9%,var(--surface-2))]' : ''
            }`.trim()}
            onDragEnter={() => setDragging(true)}
            onDragLeave={() => setDragging(false)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={chooseDroppedFile}
          >
            <input
              className="hidden"
              type="file"
              accept=".torrent,application/x-bittorrent"
              disabled={submitting}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = '';
                uploadTorrent(file);
              }}
            />
            <FileVideo className="size-6 text-(--input-accent)" />
            <strong className="max-w-full break-all text-xs">{torrentFile?.name ?? t('dropTorrentFile')}</strong>
            <small className="text-[9px] text-(--muted)">
              {torrentFile ? formatBytes(torrentFile.size) : t('torrentFileLimit')}
            </small>
          </label>
        </section>
      </section>
      {submitting && (
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-(--muted)">
          <LoaderCircle className="size-4 text-(--input-accent) spin" />
          <span>{download ? t('preparingPlayback') : t('parsingTorrent')}</span>
        </div>
      )}
      {fileSelectionDialog}
    </section>
  );
};
